import { map } from "lodash-es";
import { useStyles } from "./Settings.style";
import { Icon } from "../../components/core/Icon/Icon";
import AppBar from "../../components/common/AppBar/AppBar";
import { Typography } from "../../components/core/Typography/Typography";
import { System } from "../../models/systems";
import { TypographyColor, TypographyVariant } from "../../constants/typography";
import { useMutation, useQuery } from "@apollo/client";
import { meGQL, updateUserGQL } from "@onmo/graphql/graphql";
import Button from "../../components/core/Button/Button";
import { PageRoutes } from "../../constants/historyGaming";
import { Toggle } from "../../components/core/Toggle";
import { useState } from "react";
import { NotificationErrorPopup } from "../../components/common/NotificationErrorPopup";
import { IOSInstallPopup } from "../../components/common/PopupAddHomeScreen";
import { LogoutPopup } from "../../components/common/LogoutPopup";
import { useAppDispatch } from "../../components/hooks";
import { addPopupQueue } from "../../store/slices/popup";
import { PopupType } from "../../constants/popup";
import { DeleteAccountPopup } from "../../components/common/DeleteAccountPopup";
import { HelpShift } from "../../components/common/HelpShift";
import { User } from "../../models/user";
import { User as UserGraphql } from "@onmo/graphql/graphql";
import { showSnackbar } from "../../store/slices/alert";
import { NotificationPermission } from "../../models/pushNotification";
import { useTranslation } from "react-i18next";
import { PwaDownload } from "../../components/common/PwaDownload";
import { HistoryGaming } from "../../models/historyGaming";
import { useNavigate } from "react-router-dom";

export const Settings = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isOpenHelpShift, setIsOpenHelpShift] = useState(false);
  const { data: dataMe } = useQuery(meGQL);
  const [updateUserMutation] = useMutation(updateUserGQL);

  const [isErrorEnableNoti, setIsErrorEnableNoti] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnableNotification, setEnableNotification] = useState(
    dataMe?.me && dataMe?.me.pushSubscription !== "null",
  );

  const menus = [
    {
      category: t("settings_Account"),
      value: [
        {
          icon: (
            <Icon name="icon-linear-profile-circle" className={classes.icon} />
          ),
          title: "User Profile",
          action: () => navigate(`${PageRoutes.Profile}`),
        },
        {
          icon: <Icon name="icon-linear-global" className={classes.icon} />,
          title: t("settings_Languages", { count: 100 }),
          action: () => navigate(`${PageRoutes.Languages}`),
        },
        {
          icon: <Icon name="icon-linear-notification" size={18} />,
          title: t("settings_Notifications"),
          isHide: !System.isSupportPushNotification(),
          showToggle: true,
        },
        {
          icon: (
            <Icon name="icon-linear-profile-2user" className={classes.icon} />
          ),
          title: "Friends",
          action: () => navigate(`${PageRoutes.Friends}`),
        },
      ],
    },
    {
      category: t("settings_Gear up"),
      value: [
        {
          icon: <Icon name="icon-shopping-bag1" className={classes.icon} />,
          title: t("shop_Shop"),
          action: () => navigate(`${PageRoutes.Shop}`),
        },
        {
          icon: <Icon name="icon-medal" size={18} />,
          title: t("settings_My Level & Gems"),
          action: () => navigate(`${PageRoutes.Level}`),
        },
      ],
    },
    {
      category: t("settings_Support"),
      value: [
        {
          icon: <Icon name="icon-message-question" size={18} />,
          title: t("settings_FAQ"),
          action: () => navigate(`${PageRoutes.Faq}`),
        },
        {
          icon: <Icon name="icon-headphone" size={18} />,
          title: t("settings_Help"),
          action: () => setIsOpenHelpShift(true),
        },
        {
          icon: <Icon name="icon-shield-security" size={18} />,
          title: t("settings_Privacy policy"),
          action: () => navigate(`${PageRoutes.PrivacyPolicy}`),
        },
        {
          icon: <Icon name="icon-document" size={18} />,
          title: t("settings_Terms and conditions"),
          action: () => navigate(`${PageRoutes.TermsAndConditions}`),
        },
        {
          icon: <Icon name="icon-monitor" size={18} />,
          title: t("settings_Cookie Policy"),
          action: () => navigate(`${PageRoutes.CookiePolicy}`),
        },
      ],
    },
  ];

  const onSubmitNotification = async () => {
    setIsLoading(true);
    try {
      const pushSubscription = await User.togglePushSubscription(dataMe?.me);
      if (pushSubscription) {
        if (pushSubscription !== "null") {
          window.clevertap?.notifications.push({
            serviceWorkerPath: "/service-worker.js",
          });
        }
        await updateUserMutation({
          variables: {
            input: { pushSubscription },
          },
          update: (cache) => {
            const existingMeData = cache.readQuery<{ me: UserGraphql }>({
              query: meGQL,
            });
            // Update me Xp
            if (existingMeData?.me) {
              cache.writeQuery({
                query: meGQL,
                data: {
                  me: {
                    ...existingMeData.me,
                    pushSubscription,
                  },
                },
              });
            }
          },
        });
      }
      setEnableNotification(!isEnableNotification);
    } catch (e) {
      dispatch(showSnackbar((e as Error)?.message));
    }
    setIsLoading(false);
  };

  const onRequestPushNotification = async () => {
    if (isLoading) return;
    // Check Notification API is available then request open permission popup in your browser
    if (
      typeof Notification !== "undefined" &&
      Notification.permission !== NotificationPermission.Granted
    ) {
      const permission = await window.Notification.requestPermission();
      if (permission === NotificationPermission.Denied) {
        setEnableNotification(!isEnableNotification);
      }
    } else {
      await onSubmitNotification();
    }
  };

  const onCopy = () => {
    navigator.clipboard.writeText(dataMe?.me.id || "");
    dispatch(showSnackbar(t("snackbar_Copied")));
  };

  return (
    <div className={classes.root}>
      <AppBar
        leftItems={
          <Icon
            name="icon-bold-arrow-left-3"
            onClick={() => HistoryGaming.back(navigate)}
            data-testid="back-icon"
          />
        }
        centerItems={
          <Typography
            variant={TypographyVariant.BodyB4Medium}
            data-testid="settings-title"
          >
            {t("settings_Settings")}
          </Typography>
        }
      />
      <div className={classes.content}>
        <PwaDownload className={classes.banner} />
        {map(menus, (menuItems, index) => (
          <div key={index} className={classes.menuItem}>
            <Typography
              variant={TypographyVariant.HeadingH5Bold}
              data-testid={menuItems.category.replace(/ /g, "-").toLowerCase()}
            >
              {menuItems.category}
            </Typography>
            {map(menuItems.value, (item, index) => {
              if (item?.isHide) return null;
              return (
                <div
                  key={index}
                  className={classes.item}
                  onClick={item?.action}
                >
                  {item.icon}
                  <Typography
                    variant={TypographyVariant.BodyB5Medium}
                    className={classes.title}
                    data-testid={item.title.replace(/ /g, "-").toLowerCase()}
                  >
                    {item.title}
                  </Typography>
                  {item?.showToggle ? (
                    <Toggle
                      onClick={onRequestPushNotification}
                      checked={isEnableNotification}
                    />
                  ) : (
                    <Icon name="icon-bold-arrow-right-3" size={18} />
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <div>
          <Typography
            variant={TypographyVariant.BodyB5Medium}
            color={TypographyColor.Silver}
          >
            {t("settings_Version")}: {import.meta.env.VITE_BUILD_VERSION}{" "}
          </Typography>
          <div className={classes.userId}>
            <Typography
              variant={TypographyVariant.BodyB6Regular}
              color={TypographyColor.Gray}
            >
              {t("settings_User ID")}: {dataMe?.me.id}
            </Typography>
            <Icon name="icon-copy" size={14} onClick={onCopy} />
          </div>
        </div>
        <div className={classes.buttonGroup}>
          {/* <Button
            variant="outlined"
            fullWidth
            onClick={() =>
              dispatch(addPopupQueue({ type: PopupType.DeleteAccount }))
            }
          >
            {t("settings_Delete Account")}
          </Button> */}
          {/* <Button
            variant="contained"
            fullWidth
            onClick={() => dispatch(addPopupQueue({ type: PopupType.Logout }))}
          >
            {t("settings_Logout")}
          </Button> */}
        </div>
      </div>
      <NotificationErrorPopup
        open={isErrorEnableNoti}
        onClose={() => setIsErrorEnableNoti(false)}
      />
      <IOSInstallPopup />
      <LogoutPopup />
      <DeleteAccountPopup />
      {isOpenHelpShift && (
        <HelpShift onClose={() => setIsOpenHelpShift(false)} />
      )}
    </div>
  );
};
